import type { Editor } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { CellSelection } from "@tiptap/pm/tables"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

/**
 * Focus plugin based on `extension-focus`, but only for table cells and with
 * a small fix to prevent focus jumping when it's a cell selection.
 * Applies a className of `focusedCell` to the cell that is focused.
 * See: https://github.com/ueberdosis/tiptap/blob/v2.11.5/packages/extension-focus/src/focus.ts
 */
export function tableCellFocusPlugin(editor: Editor) {
  return new Plugin({
    key: new PluginKey("tableCellFocus"),
    props: {
      decorations(state) {
        const { doc, selection } = state
        const { isEditable, isFocused } = editor

        // Checking if it's a cell selection fixes an issue where after selecting
        // a whole row/column, focusing out of the editor and then back in on a
        // different cell, the focus jumped to the wrong cell (often the first
        // cell of the previous row/column selection).
        // See: https://github.com/prismicio/editor/pull/1651#discussion_r2014882294
        if (!isEditable || !isFocused || selection instanceof CellSelection) {
          return DecorationSet.create(doc, [])
        }

        const decorations: Decoration[] = []
        const { anchor } = selection

        doc.descendants((node, pos) => {
          if (node.isText) return false

          const isCurrent = anchor >= pos && anchor <= pos + node.nodeSize - 1
          if (!isCurrent) return false

          if (
            node.type.name === "tableCell" ||
            node.type.name === "tableHeader"
          ) {
            decorations.push(
              Decoration.node(pos, pos + node.nodeSize, {
                class: "focusedCell",
              }),
            )
            return false
          }

          return true
        })

        return DecorationSet.create(doc, decorations)
      },
    },
  })
}
